import { create } from 'zustand';
import type { User as FirebaseUser } from 'firebase/auth';
import { resolveBranding, type BrandingData } from '@scio3/shared-types';
import type { MeResponse } from '@scio3/shared-types';
import type { CompanyInfo } from '@scio3/shared-types';
import { isWhiteLabelBuild } from '@/lib/app-variant';

const AWS_BUCKET = (import.meta.env.VITE_AWS_BUCKET_NAME as string | undefined) ?? '';

/**
 * Post-login branding — the scio2 `AuthService.branding` / `principal.formatDomainData` analog.
 *
 * The backend returns `me.brandingData` as the raw domain doc (S3 keys, and NO `isWhiteLabel`
 * field). We:
 *   1. force `isWhiteLabel` from the BUILD flag (`VITE_IS_WHITE_LABEL`) — the white-label build
 *      only ever serves white-label customers; the standard app.optisigns.com build is `false`
 *      (this is the scio2 `OPS_IS_WHITE_LABEL` analog, replacing scio2's hostname-allowlist check);
 *   2. run `resolveBranding`, which prepends `https://{bucket}.s3.amazonaws.com/` to
 *      logo/logoCompany/favicon/backgoundLogin and defaults logoCompany→'OS' (matches scio2).
 *
 * The result is mirrored onto BOTH `me.brandingData` and `company.brandingData` so every consumer
 * (TopNavigation logo, the `*.brandingData.isWhiteLabel` gates, etc.) reads one resolved object —
 * `GET_COMPANY_INFO` does not fetch `brandingData`, so `company.brandingData` was previously always
 * undefined.
 */
function formatBranding(raw: Partial<BrandingData> | undefined | null): BrandingData {
  return resolveBranding({ ...(raw ?? {}), isWhiteLabel: isWhiteLabelBuild() }, AWS_BUCKET);
}

interface AuthState {
  firebaseUser: FirebaseUser | null;
  token: string | null;
  me: MeResponse | null;
  company: CompanyInfo | null;
  /** True once GET_COMPANY_INFO has settled (success or failure) — distinct from company !== null. */
  companyLoaded: boolean;
  loading: boolean;
  /** True after confirmed handshake with SCIO 1.0 parent (custom token auth succeeded). */
  isEmbedded: boolean;
  /** True during social signup/login to prevent AuthProvider's orphan recovery race condition. */
  signupInProgress: boolean;
  /** True when me.accountStatus is explicitly false (payment lapsed). */
  isAccountInactive: boolean;
  /** True when user has been removed from all teams (teams is null or empty). */
  hasNoTeams: boolean;
  /** True when user must change password before accessing the app. */
  needsPasswordChange: boolean;
  /** Pending account switch invitation from ME_QUERY (inviteUser field). */
  inviteUser: MeResponse['inviteUser'] | null;
  /** True when orphaned Firebase user needs reCAPTCHA-gated recovery via OrphanRecoveryDialog. */
  needsOrphanRecovery: boolean;
  /** Payload for orphan recovery (firstName/lastName extracted from Firebase displayName). */
  orphanRecoveryPayload: { firstName: string; lastName?: string } | null;
  /** Incremented to trigger AuthProvider's loadUserContext re-run after orphan recovery completes. */
  loadUserContextRetry: number;
  /** Firebase sign_in_provider from JWT claims — set once on login, used to skip dual-live routing for SAML (#709). */
  signInProvider: string | null;
  /** True when signup just completed and onboarding should show immediately (avoids flash). */
  pendingOnboarding: boolean;

  setAuth: (data: { firebaseUser: FirebaseUser; token: string }) => void;
  setToken: (token: string) => void;
  setMe: (me: MeResponse) => void;
  setCompany: (company: CompanyInfo) => void;
  setCompanyLoaded: () => void;
  /** Optimistically mark that the user has assigned content to a screen for the
   *  first time. Flips `company.payment.firstScreenAssignmentEventFired` from
   *  'not_triggered' to a timestamp so first-run gates (Edit Screen tutorial,
   *  Get Started checklist) immediately stop showing within the session — the
   *  backend persists the same flag for future sessions. No-op once already set. */
  markFirstScreenAssignment: () => void;
  setEmbedded: (embedded: boolean) => void;
  setLoading: (loading: boolean) => void;
  setSignupInProgress: (inProgress: boolean) => void;
  setPendingOnboarding: (pending: boolean) => void;
  setInviteUser: (inviteUser: MeResponse['inviteUser']) => void;
  setNeedsOrphanRecovery: (
    needs: boolean,
    payload?: { firstName: string; lastName?: string },
  ) => void;
  incrementLoadUserContextRetry: () => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  firebaseUser: null,
  token: null,
  me: null,
  company: null,
  companyLoaded: false,
  loading: true,
  isEmbedded: false,
  signupInProgress: false,
  isAccountInactive: false,
  hasNoTeams: false,
  needsPasswordChange: false,
  inviteUser: null,
  needsOrphanRecovery: false,
  orphanRecoveryPayload: null,
  loadUserContextRetry: 0,
  signInProvider: null,
  pendingOnboarding: false,

  setAuth: ({ firebaseUser, token }) =>
    set((state) => {
      // Extract sign_in_provider from JWT claims once on login (#709)
      let signInProvider: string | null = null;
      try {
        const claims = JSON.parse(atob(token.split('.')[1]));
        signInProvider = claims.firebase?.sign_in_provider ?? null;
      } catch {
        /* malformed token */
      }
      return {
        firebaseUser,
        token,
        signInProvider,
        // Only reset loading when token actually changes (new auth session).
        // Redundant calls with the same token (e.g. LoginPage after onAuthStateChanged)
        // must NOT reset loading — otherwise loadUserContext won't re-run to clear it.
        loading: state.token !== token ? true : state.loading,
      };
    }),
  setToken: (token) => set({ token }),
  setMe: (me) =>
    set((state) => {
      const brandingData = formatBranding(me.brandingData as Partial<BrandingData> | undefined);
      return {
        me: { ...me, brandingData },
        // Mirror onto company so `company.brandingData.*` readers work regardless of fetch order.
        company: state.company ? { ...state.company, brandingData } : state.company,
        isAccountInactive: me.accountStatus === false,
        hasNoTeams: !me.teams || me.teams.length === 0,
        needsPasswordChange: me.passwordChangeRequire === true,
      };
    }),
  setCompany: (company) =>
    set((state) => ({
      company: {
        ...company,
        // company.brandingData is sourced from me.brandingData (GET_COMPANY_INFO doesn't fetch it).
        brandingData: state.me?.brandingData ?? formatBranding(undefined),
      },
    })),
  setCompanyLoaded: () => set({ companyLoaded: true }),
  markFirstScreenAssignment: () =>
    set((state) => {
      const company = state.company;
      // Already triggered (timestamp present) or no company loaded → nothing to do.
      if (
        !company?.payment ||
        (company.payment.firstScreenAssignmentEventFired ?? 'not_triggered') !== 'not_triggered'
      ) {
        return {};
      }
      return {
        company: {
          ...company,
          payment: {
            ...company.payment,
            firstScreenAssignmentEventFired: new Date().toISOString(),
          },
        },
      };
    }),
  setEmbedded: (embedded) => set({ isEmbedded: embedded }),
  setLoading: (loading) => set({ loading }),
  setSignupInProgress: (signupInProgress) => set({ signupInProgress }),
  setPendingOnboarding: (pendingOnboarding) => set({ pendingOnboarding }),
  setInviteUser: (inviteUser) => set({ inviteUser }),
  setNeedsOrphanRecovery: (needs, payload) =>
    set({ needsOrphanRecovery: needs, orphanRecoveryPayload: payload ?? null }),
  incrementLoadUserContextRetry: () =>
    set((state) => ({ loadUserContextRetry: state.loadUserContextRetry + 1 })),
  clearAuth: () =>
    set({
      firebaseUser: null,
      token: null,
      me: null,
      company: null,
      companyLoaded: false,
      loading: false,
      signupInProgress: false,
      pendingOnboarding: false,
      isAccountInactive: false,
      hasNoTeams: false,
      needsPasswordChange: false,
      inviteUser: null,
      needsOrphanRecovery: false,
      orphanRecoveryPayload: null,
      loadUserContextRetry: 0,
      signInProvider: null,
    }),
}));

// Expose the store on window ONLY for local dev + explicit E2E builds. Used by
// share-link e2e tests to detect fake-me injection and other auth-state
// regressions: `window.__useAuthStore.getState().me?._id` etc.
//
// Excluded from staging / preview / sandbox builds (`import.meta.env.PROD=false`
// but not DEV-mode) — leaking auth state to window in user-facing non-prod
// environments lets any third-party script read `me`, `firebaseUser`, etc. via
// the global. Gate strictly on DEV or an explicit E2E flag set by the runner.
if (
  typeof window !== 'undefined' &&
  (import.meta.env.DEV || import.meta.env.VITE_E2E_MODE === 'true')
) {
  (window as unknown as { __useAuthStore: typeof useAuthStore }).__useAuthStore = useAuthStore;
}
